import React, { createContext, useContext, useState, useEffect } from "react";
import { AUTH_CONFIG, validateCredentials } from "../config/auth";

type AuthState = {
  isLoggedIn: boolean;
  username: string;
  error: string | null;
  requiresMFA: boolean;
  mfaPending: boolean;
};

type AuthContextType = AuthState & {
  login: (username: string, password: string) => Promise<boolean>;
  verifyMFA: (code: string) => Promise<boolean>;
  logout: () => void;
};

// Create the context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Session storage keys
const SESSION_KEY = "dental_portal_session";

// Provider Component
export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  // Initialize state from session storage
  const [authState, setAuthState] = useState<AuthState>(() => {
    const savedSession = sessionStorage.getItem(SESSION_KEY);
    if (savedSession) {
      try {
        return JSON.parse(savedSession);
      } catch {
        return {
          isLoggedIn: false,
          username: "",
          error: null,
          requiresMFA: false,
          mfaPending: false,
        };
      }
    }
    return {
      isLoggedIn: false,
      username: "",
      error: null,
      requiresMFA: false,
      mfaPending: false,
    };
  });

  // Update session storage when auth state changes
  useEffect(() => {
    if (authState.isLoggedIn) {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(authState));
    } else {
      sessionStorage.removeItem(SESSION_KEY);
    }
  }, [authState]);

  // Simulate sending MFA code (in real app, this would call an API)
  const sendMFACode = async (username: string): Promise<void> => {
    await new Promise((resolve) => setTimeout(resolve, 1000)); // Simulate API delay
    console.log(`MFA code sent to ${username}`);
  };

  // First step of login - validate credentials
  const login = async (username: string, password: string) => {
    setAuthState((prev) => ({ ...prev, error: null }));

    try {
      if (!username || !password) {
        throw new Error(AUTH_CONFIG.messages.missingCredentials);
      }

      const validation = validateCredentials(username, password);
      if (validation.isValid) {
        if (validation.requireMFA) {
          // Only initiate MFA if required
          await sendMFACode(username);
          setAuthState((prev) => ({
            ...prev,
            username,
            requiresMFA: true,
            mfaPending: true,
          }));
        } else {
          // If MFA not required, log in directly
          setAuthState((prev) => ({
            ...prev,
            username,
            isLoggedIn: true,
            requiresMFA: false,
            mfaPending: false,
          }));
        }
        return true;
      } else {
        throw new Error(AUTH_CONFIG.messages.invalidCredentials);
      }
    } catch (error) {
      setAuthState((prev) => ({
        ...prev,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      }));
      return false;
    }
  };

  // Second step - verify MFA code
  const verifyMFA = async (code: string): Promise<boolean> => {
    try {
      // In a real app, validate the code against a backend
      if (code === "123456") {
        // Example validation
        setAuthState((prev) => ({
          ...prev,
          isLoggedIn: true,
          requiresMFA: false,
          mfaPending: false,
          error: null,
        }));
        return true;
      }
      throw new Error("Invalid MFA code");
    } catch (error) {
      setAuthState((prev) => ({
        ...prev,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      }));
      return false;
    }
  };

  const logout = () => {
    setAuthState({
      isLoggedIn: false,
      username: "",
      error: null,
      requiresMFA: false,
      mfaPending: false,
    });
  };

  return (
    <AuthContext.Provider
      value={{
        ...authState,
        login,
        verifyMFA,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook for using auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;
